import { AtarimEditorRef } from 'editor';
import { AnimatePresence } from 'motion/react';
import { useEffect, useRef } from 'react';
import { useAppSelector } from '../../../store';
import { getCurrentTaskState, getSelectedTaskId, getShouldShowSkeleton } from '../../../store/tasksSlice';
import { useCreateTask } from '../createTask';
import { TypingIndicator } from '../typing-indicator';
import { ActionBar } from './action-bar/action-bar';
import { Activity } from './activity/activity';
import PhotoEditor from './annotate/annotate-editor';
import { CommentBox } from './comment/comment-box';
import { FileDisplay } from './file/file';
import { Header } from './header/header';

interface Props {
    fileOpen: () => void;
    files: File[];
    setFiles: React.Dispatch<React.SetStateAction<File[]>>;
    comment: string;
    setComment: React.Dispatch<React.SetStateAction<string>>;
}

export const Task = ({ fileOpen, files, setFiles, comment, setComment }: Props) => {
    const { isPending } = useAppSelector(getCurrentTaskState);
    const taskId = useAppSelector(getSelectedTaskId);
    const editorRef = useRef<AtarimEditorRef | null>(null);
    const { handleCreate, isSuccess } = useCreateTask({
        existingTask: !isPending,
        taskType: 'Page',
        setFiles,
        setComment
    });
    const shouldShowSkeleton = useAppSelector(getShouldShowSkeleton);

    useEffect(() => {
        if (!shouldShowSkeleton || isPending) {
            setTimeout(() => {
                editorRef.current?.focus?.({ preventScroll: true });
            }, 200);
        }
    }, [shouldShowSkeleton, isPending]);

    return (
        <>
            <AnimatePresence mode="wait">
                <div className="flex flex-col gap-2 ring-0 outline-none">
                    <Header />
                    <Activity />
                    <div className="flex flex-col gap-1 relative pt-1">
                        {taskId && <TypingIndicator taskId={taskId} />}
                        <CommentBox
                            editorRef={editorRef}
                            isSuccess={isSuccess}
                            fileOpen={fileOpen}
                            handleCreate={handleCreate}
                            files={files}
                            setFiles={setFiles}
                            comment={comment}
                            setComment={setComment}
                        />
                    </div>
                    {files.length > 0 && (
                        <>
                            <div className="flex flex-col gap-2 max-h-[135px] overflow-auto">
                                {files.length > 0 && (
                                    <>
                                        <div className="grid grid-cols-5 gap-2">
                                            {files
                                                .filter(f => f.type.startsWith('image/'))
                                                .map((f) => (
                                                    <FileDisplay file={f} removable key={f.name} isPendingFile={true} setFiles={setFiles} />
                                                ))}
                                        </div>
                                        <div className="grid grid-cols-1 gap-2">
                                            {files
                                                .filter(f => !f.type.startsWith('image/'))
                                                .map((f) => (
                                                    <FileDisplay file={f} removable key={f.name} isPendingFile={true} setFiles={setFiles} />
                                                ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </>
                    )}

                    <ActionBar
                        editorRef={editorRef}
                        handleCreate={handleCreate}
                        files={files}
                        comment={comment}
                        setComment={setComment}
                        setFiles={setFiles}
                    />
                </div>
            </AnimatePresence>
            <PhotoEditor setFiles={setFiles} files={files} />
        </>
    );
};